/**
 * ComponentsScreen — Biblioteca de Componentes (Fase 3 · Bloco 2).
 *
 * Apresenta componentes JÁ criados e testados em OUTROS projetos do tenant, para
 * REAPROVEITAR neste. "Buscar componentes reutilizáveis" → POST .../components/suggest:
 * o backend lê o registro de reuso (reusable_components de outros projetos) e o
 * arquiteto de reuso (via Claude) VALIDA quais fazem sentido para este projeto,
 * comparando com o escopo/memória atual. Sem registro → mensagem de "nada salvo ainda".
 * Marcar um componente (PATCH) grava na memória para a EXECUÇÃO reutilizá-lo. Lê ?project=.
 */
import { C } from '@/theme/palette';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useComponents, useSuggestComponents, useToggleComponent } from '@/api/techScope';
import { AiTruncationNotice } from '@/components/ui';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

export function ComponentsScreen({ projectId: wizardProjectId } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;
  const { data, isLoading } = useComponents(projectId);
  const suggest = useSuggestComponents(projectId);
  const toggle = useToggleComponent(projectId);

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;
  const comps = data?.components ?? [];
  const savedTotal = comps.filter((c) => c.selected).reduce((a, c) => a + (c.tokensSaved || 0), 0);
  const selCount = comps.filter((c) => c.selected).length;
  // Distingue "ainda não buscou" de "buscou e a biblioteca está vazia".
  const searched = suggest.isSuccess || suggest.data;

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Biblioteca de Componentes</h2>
        {comps.length > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 10px', borderRadius: 6,
            color: C.accent, background: C.surface2 }}>
            {selCount} p/ reuso · economia −{savedTotal.toLocaleString('pt-BR')} tk
          </span>
        )}
        <button onClick={() => suggest.mutate()} disabled={suggest.isPending}
          style={{ marginLeft: 'auto', cursor: suggest.isPending ? 'default' : 'pointer',
            background: C.accent, color: C.black, border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 13, fontWeight: 700, opacity: suggest.isPending ? 0.6 : 1 }}>
          {suggest.isPending ? 'Buscando…' : comps.length ? '↻ Revalidar reuso' : 'Buscar componentes reutilizáveis'}
        </button>
      </div>
      <p style={{ color: C.dim, maxWidth: '74ch', marginTop: 10, fontSize: 13.5 }}>
        Componentes <strong style={{ color: C.text }}>já criados e testados em outros projetos</strong> do
        tenant, disponíveis para reaproveitar aqui. O arquiteto de reuso (via AI Core/Claude) valida
        quais são compatíveis com a stack e o escopo <strong style={{ color: C.text }}>já na memória</strong> deste
        projeto. Marque os que quer reutilizar — a decisão vai para a memória e a execução usa o componente
        pronto (não recria).
      </p>

      {suggest.isError && (
        <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>
          Falha: {suggest.error?.status === 409
            ? 'gere o Blueprint antes de buscar componentes reutilizáveis.'
            : String(suggest.error?.message || suggest.error)}
        </div>
      )}
      {isLoading && <div style={{ marginTop: 20, color: C.dim }}>Carregando…</div>}
      {!isLoading && comps.length === 0 && (
        <div style={{ marginTop: 24, padding: 32, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          {searched ? (
            <>
              <div style={{ fontSize: 26, marginBottom: 8 }}>📭</div>
              <strong style={{ color: C.text, fontSize: 15 }}>
                Não temos componentes salvos ainda para reaproveitar.
              </strong>
              <p style={{ margin: '8px auto 0', maxWidth: '52ch', fontSize: 13 }}>
                Componentes criados e testados em projetos concluídos deste tenant aparecerão aqui
                para reuso — validados contra o escopo do projeto atual. Este é o primeiro (ou nenhum
                projeto anterior registrou componentes compatíveis).
              </p>
            </>
          ) : (
            <>
              Clique em <strong style={{ color: C.text }}>Buscar componentes reutilizáveis</strong> para
              procurar componentes de outros projetos compatíveis com este escopo.
            </>
          )}
        </div>
      )}

      <AiTruncationNotice complete={data?.aiComplete} onRetry={() => suggest.mutate()} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 12, marginTop: 18 }}>
        {comps.map((c) => (
          <div key={c.id} style={{ border: `1px solid ${c.selected ? C.accent : C.border}`,
            background: C.surface, borderRadius: 12, padding: 16, opacity: c.compatible ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ cursor: c.compatible ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <input type="checkbox" checked={c.selected} disabled={!c.compatible || toggle.isPending}
                  onChange={(e) => toggle.mutate({ id: c.id, selected: e.target.checked })} />
                <strong style={{ fontSize: 13.5 }}>{c.name}</strong>
              </label>
              {c.compatible && (
                <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 8px', borderRadius: 6,
                  background: 'rgba(1,230,228,0.12)', color: C.accent }}>compatível</span>
              )}
            </div>
            <p style={{ color: C.dim, fontSize: 12.5, margin: '8px 0 0' }}>{c.description}</p>
            {c.reason && (
              <p style={{ color: C.muted, fontSize: 12, margin: '6px 0 0', fontStyle: 'italic' }}>
                Por que reusar: {c.reason}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 6,
                background: C.surface2, color: C.accent }}>validado {c.coveragePct}%</span>
              <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 6,
                background: C.surface2, color: C.dim }}>−{(c.tokensSaved || 0).toLocaleString('pt-BR')} tk</span>
              {c.usedIn > 0 && (
                <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 6,
                  background: C.surface2, color: C.dim }}>usado em {c.usedIn} projeto{c.usedIn > 1 ? 's' : ''}</span>
              )}
              {c.origin && (
                <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 6,
                  background: C.surface2, color: C.muted }}>origem: {c.origin}</span>
              )}
              {!c.compatible && (
                <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 6,
                  background: 'rgba(255,51,0,0.12)', color: C.bad }}>incompatível</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
